import { Instagram, Music, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const trackEvent = (episode_number, action) => {
  try {
    let sess = localStorage.getItem("iwk_session");
    if (!sess) { sess = crypto.randomUUID?.() || String(Date.now()); localStorage.setItem("iwk_session", sess); }
    api.post("/track/evidence", { episode_number, action, session_id: sess }).catch(() => {});
  } catch { /* ignore */ }
};

/**
 * Joey's Evidence Locker reveal — shown once a listener has played the episode.
 * Renders the original screenshot + 3 share buttons:
 *  - Instagram (4:5 branded variant)
 *  - TikTok / Reels (9:16 branded variant)
 *  - Copy link to original
 */
export default function EvidenceLockerReveal({ ep, images }) {
  if (!images?.original) return null;
  const abs = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);
  const displayNumber = String(ep.parent_number || ep.number).padStart(2, "0");

  const downloadVariant = async (variant, label) => {
    const url = abs(images[variant]);
    trackEvent(ep.number, `download-${variant}`);
    const t = toast.loading(`saving ${label}...`);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `if-we-knew-ep${displayNumber}-${variant}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success(`${label} downloaded ↓`, { id: t });
    } catch {
      toast.error("could not download", { id: t });
    }
  };

  const copyOriginalLink = async () => {
    trackEvent(ep.number, "copy-link");
    try {
      await navigator.clipboard.writeText(abs(images.original));
      toast.success("link copied");
    } catch {
      toast.error("could not copy");
    }
  };

  return (
    <div className="mt-4 bg-ink border-l-4 border-hotpink p-3 md:p-4" data-testid={`evidence-locker-${ep.number}`}>
      <p className="scribble text-hotpink text-lg rotate-[-1deg] inline-block leading-none mb-2">joey&rsquo;s evidence locker</p>
      <img
        src={abs(images.original)}
        alt={`Evidence for episode ${displayNumber}`}
        data-testid={`evidence-image-${ep.number}`}
        className="block max-w-full max-h-[480px] object-contain bg-coal mt-1 mb-3"
        loading="lazy"
      />
      <p className="font-outfit text-white/40 text-[10px] uppercase tracking-widest mb-3">unlocked because you listened ♥</p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => downloadVariant("ig", "Instagram 4:5")}
          data-testid={`evidence-share-ig-${ep.number}`}
          className="flex items-center gap-2 bg-hotpink text-white hover:bg-white hover:text-ink font-outfit font-extrabold uppercase tracking-widest text-[10px] px-3 py-2 transition-colors"
        >
          <Instagram size={12} /> share to instagram (4:5)
        </button>
        <button
          onClick={() => downloadVariant("tiktok", "TikTok 9:16")}
          data-testid={`evidence-share-tiktok-${ep.number}`}
          className="flex items-center gap-2 bg-coal border border-white/15 hover:border-hotpink text-white/80 hover:text-hotpink font-outfit font-extrabold uppercase tracking-widest text-[10px] px-3 py-2 transition-colors"
        >
          <Music size={12} /> tiktok (9:16)
        </button>
        <button
          onClick={copyOriginalLink}
          data-testid={`evidence-copy-link-${ep.number}`}
          className="flex items-center gap-2 bg-coal border border-white/15 hover:border-hotpink text-white/80 hover:text-hotpink font-outfit font-extrabold uppercase tracking-widest text-[10px] px-3 py-2 transition-colors"
        >
          <Copy size={12} /> copy link
        </button>
        <a
          href={abs(images.original)}
          download={`if-we-knew-ep${displayNumber}-evidence.jpg`}
          onClick={() => trackEvent(ep.number, "download-original")}
          data-testid={`evidence-download-original-${ep.number}`}
          className="flex items-center gap-2 text-white/40 hover:text-hotpink font-outfit text-[10px] uppercase tracking-widest font-bold ml-auto"
          title="Download the original image"
        >
          <Download size={11} /> original
        </a>
      </div>
    </div>
  );
}
